import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import UserList from "../components/user-list";
import UserAdd from "../components/user-add";
import EditUser from "../components/user-edit";
import UserActionConfirm from "../components/user-action-confirm";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import FlowLoader from "../components/FlowLoader";
import AdminPageHeader from "../components/admin-page-header";

import "../styles/admin.css";
import { FullUserDetails } from "../types/User";
import { displayUserType } from "../types/User";
import DeleteUser from "../components/user-delete";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useAccess } from "../access/AccessContext";
import { usernameErrorKey } from "../components/helpers/usernameRules";
import type { ResetPasswordPayload } from "../components/user-edit";
import type { TwoFactorResetOutcome } from "../components/users/TwoFactorResetDialog";

// Lifted out of user-list.tsx so the same confirmation modal can sit
// over either the table OR the edit modal — the edit modal's icon
// buttons hand off to the same `pending` state.
type PendingAction =
    | { kind: "force"; user: displayUserType }
    | { kind: "suspend"; user: displayUserType }
    | { kind: "unsuspend"; user: displayUserType }
    | null;

const AdminUsers = () => {
    const navigator = useNavigate();
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    // Create has its own key (admin.users.create, 2026-09-30); before the access map settles, the
    // page's read-only state decides as before.
    const { authoritative, canWrite } = useAccess();
    const canCreate = !readOnly && (!authoritative || canWrite("admin.users.create"));

    const blankUser = { _id: "", UID: 0, name: "", permissions: "", warehouse: "", userRFID: "" };

    const [users, set_Users] = useState<displayUserType[]>([]);
    const [toggle_addUser, set_Toggle_addUser] = useState<boolean>(false);
    const [toggle_editUser, set_Toggle_editUser] = useState<boolean>(false);
    const [toggle_deleteUser, set_toggle_deleteUser] = useState<boolean>(false);
    const [editUsersDetails, set_editUsersDetails] = useState<displayUserType>(blankUser);
    const [deleteUsersDetails, set_deleteUsersDetails] = useState<displayUserType>(blankUser);
    const [pending, setPending] = useState<PendingAction>(null);
    const [submitting, setSubmitting] = useState(false);

    // Always reflect the freshest version of the user being edited so
    // that an action invoked from inside the modal (suspend / force
    // change) flips the modal's own buttons immediately, without the
    // operator having to close + reopen.
    const liveEditUser = useMemo(
        () => users.find(u => u.UID === editUsersDetails.UID) ?? editUsersDetails,
        [users, editUsersDetails],
    );

    // Identity of whoever's currently signed in. Sent as `actor*` fields
    // on every user-CRUD body so the audit log records who DID the
    // action, not who was acted on (otherwise the target UID overwrites
    // the actor in the auto-logging middleware).
    const readActor = () => {
        try {
            // login.tsx writes the signed-in identity to localStorage —
            // this previously read sessionStorage and always came back
            // empty, so audit rows fell back to the target's identity.
            const raw = window.localStorage.getItem("user");
            if (!raw) return {};
            const u = JSON.parse(raw);
            return {
                actorUid: typeof u?.uid === "number" ? u.uid : (typeof u?.UID === "number" ? u.UID : undefined),
                actorName: typeof u?.name === "string" ? u.name : undefined,
                actorWarehouse: typeof u?.warehouse === "string" ? u.warehouse : undefined,
            };
        } catch { return {}; }
    };

    useEffect(() => {
        const init = async () => {
            // Request all users
            await apiFetch(`/login/users`, {
                method: "GET",
            }).then(async (res): Promise<any> => {
                let response = await res.json();

                if (res.ok) {
                    set_Users(response);
                    console.log(response);
                    console.log(users)
                } else if (res.status === 401) {
                    console.log("INVALID PERMISSIONS");
                    logoutProcessor();
                    navigator("/login");
                } else {
                    console.log(response.message);
                    toast.error(response.message);
                }
            });
        };

        init();
    }, [navigator]);

    // A username / password rejection comes back with a machine `code`
    // (reserved, profanity, taken, too_short…) — translate it; anything
    // else keeps the BE's message.
    const codedErrorMessage = (body: any): string => {
        const usernameCodes = ["required", "too_short", "too_long", "invalid_chars", "reserved", "profanity", "taken"];
        if (typeof body?.code === "string" && usernameCodes.includes(body.code)) return t(usernameErrorKey(body.code));
        return body?.message || t("server_error");
    };

    // On Add User
    const addUser = async (user: FullUserDetails) => {
        console.log(`ADDING USER ${user.name}`);

        const data = { ...user, ...readActor() };

        await apiFetch(`/login/user/new`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        }).then(async (res) => {
            const response = await res.json().catch(() => ({} as any));

            if (res.ok) {
                // New array, never an in-place push (see editUser's note).
                set_Users(prev => [...prev, response]);

                set_Toggle_addUser(false);
                console.log(`USER ${user.name} ADDED`);
                toast.success(t("user_added", { name: user.name }));
            } else {
                console.log(response.message);
                toast.error(codedErrorMessage(response));
            }
        });
    };

    // Admin sets a (temporary by default) password for a user below
    // Manager. The target's live session is evicted by the BE; the row
    // is patched optimistically so the rotation badge reflects the choice.
    const resetPassword = async (user: displayUserType, { newPassword, mustChangePassword }: ResetPasswordPayload): Promise<boolean> => {
        const data = { UID: user.UID, newPassword, mustChangePassword, ...readActor() };
        try {
            const res = await apiFetch(`/login/user/reset-password`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            if (res.ok) {
                set_Users(prev => prev.map(u => u.UID === user.UID
                    ? { ...u, mustChangePassword, passwordChangedAt: new Date().toISOString() }
                    : u));
                toast.success(t("users_reset_password_done", { name: user.name }));
                return true;
            }
            if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
                return false;
            }
            const r = await res.json().catch(() => ({}));
            toast.error(codedErrorMessage(r));
            return false;
        } catch {
            toast.error(t("server_error"));
            return false;
        }
    };

    // Admin clears a user's two-factor authentication (2026-10-20). The BE
    // may first demand the acting Admin's own current authenticator code
    // (401 two_factor_admin_code_required); the dialog then calls again with
    // `adminOtp`. A bare 401 (no code) is a dead session, as everywhere else.
    const resetTwoFactor = async (user: displayUserType, adminOtp?: string): Promise<TwoFactorResetOutcome> => {
        const data = { UID: user.UID, ...(adminOtp ? { adminOtp } : {}), ...readActor() };
        try {
            const res = await apiFetch(`/login/user/2fa/reset`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            const body = await res.json().catch(() => ({} as any));
            if (res.ok) {
                set_Users(prev => prev.map(u => u.UID === user.UID
                    ? { ...u, twoFactorEnrolledAt: null, twoFactorRequired: typeof body?.twoFactorRequired === "boolean" ? body.twoFactorRequired : u.twoFactorRequired }
                    : u));
                toast.success(t("users_reset_two_factor_done", { name: user.name }));
                return { ok: true };
            }
            if (res.status === 401 && typeof body?.code !== "string") {
                logoutProcessor();
                navigator("/login");
                return { ok: false, status: 401 };
            }
            return {
                ok: false, status: res.status,
                code: typeof body?.code === "string" ? body.code : undefined,
                message: typeof body?.message === "string" ? body.message : undefined,
                retryAfterSeconds: typeof body?.retryAfterSeconds === "number" ? body.retryAfterSeconds : undefined,
            };
        } catch {
            return { ok: false, status: 0, message: t("server_error") };
        }
    };

    // Close Add User Popup
    const closePopup = () => {
        set_Toggle_addUser(false);
    };

    // toggle delete user popup
    const toggleDelete = (user: displayUserType | null) => {

        if (user === null) {
            set_toggle_deleteUser(false);
        } else {
            set_deleteUsersDetails(user);
            set_toggle_deleteUser(true);
        }

    };

    // On Delete
    const deleteUser = async (userToDelete: displayUserType) => {
        console.log(`DELETING USER ${userToDelete.name}`);

        const data = {
            UID: userToDelete.UID,
            ...readActor(),
        };

        await apiFetch(`/login/user`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        }).then(async (res) => {
            let response = await res.json();

            if (res.ok) {
                console.log(`USER ${userToDelete.name} Deleted`);
                toast.success(t("user_deleted", { name: userToDelete.name }));

                set_Users(
                    users.filter(
                        (user: displayUserType) =>
                            user.UID !== userToDelete.UID
                    )
                );

                toggleDelete(null);

            } else {
                console.log(response.message);
                toast.error(response.message);
            }
        });
    };

    // Toggle Edit Popup
    const toggleEdit = ({ user, changesMade }: { user: displayUserType, changesMade: boolean }) => {
        if (!changesMade) {
            set_Toggle_editUser(!toggle_editUser);
            return;
        }

        set_editUsersDetails(user);
        set_Toggle_editUser(!toggle_editUser);
    };

    // Flag a user to rotate their password on next login. The list is
    // updated optimistically so the rotation-pending badge appears
    // immediately without a refetch.
    const forceChange = async (user: displayUserType) => {
        const data = { UID: user.UID, ...readActor() };
        try {
            const res = await apiFetch(`/login/user/force-password-change`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            if (res.ok) {
                set_Users(prev => prev.map(u => u.UID === user.UID ? { ...u, mustChangePassword: true } : u));
                toast.success(t("user_force_change_done", { name: user.name }));
            } else {
                const r = await res.json().catch(() => ({}));
                toast.error(r?.message || t("server_error"));
            }
        } catch {
            toast.error(t("server_error"));
        }
    };

    // Suspend / unsuspend share the same shape — different endpoint and
    // different optimistic patch. Server enforces self/superAdmin guards
    // so the FE doesn't need to repeat them defensively.
    const suspend = async (user: displayUserType) => {
        const data = { UID: user.UID, ...readActor() };
        try {
            const res = await apiFetch(`/login/user/suspend`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            if (res.ok) {
                set_Users(prev => prev.map(u => u.UID === user.UID
                    ? { ...u, status: "suspended", suspendedAt: new Date().toISOString() }
                    : u));
                toast.success(t("user_suspended", { name: user.name }));
            } else {
                const r = await res.json().catch(() => ({}));
                toast.error(r?.message || t("server_error"));
            }
        } catch {
            toast.error(t("server_error"));
        }
    };

    const unsuspend = async (user: displayUserType) => {
        const data = { UID: user.UID, ...readActor() };
        try {
            const res = await apiFetch(`/login/user/unsuspend`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            if (res.ok) {
                set_Users(prev => prev.map(u => u.UID === user.UID
                    ? { ...u, status: "active", suspendedAt: null }
                    : u));
                toast.success(t("user_unsuspended", { name: user.name }));
            } else {
                const r = await res.json().catch(() => ({}));
                toast.error(r?.message || t("server_error"));
            }
        } catch {
            toast.error(t("server_error"));
        }
    };

    // On Edit
    const editUser = async (user: displayUserType) => {
        console.log(`Editing user ${user.UID}`);

        // The LOGIN HANDLE has its own endpoint + validation vocabulary
        // (reserved / profanity / taken) and its own audit event, so it is
        // saved first and separately — only when it changed. A rejected
        // handle stops here with the modal open and nothing else touched.
        const original = users.find(u => u.UID === user.UID);
        const usernameChanged = (user.username ?? null) !== (original?.username ?? null);
        if (usernameChanged && user.username) {
            const res = await apiFetch(`/login/user/username`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ UID: user.UID, username: user.username, ...readActor() }),
            }).catch(() => null);
            if (!res) { toast.error(t("server_error")); return; }
            const body = await res.json().catch(() => ({} as any));
            if (!res.ok) {
                toast.error(codedErrorMessage(body));
                return;
            }
            // Completing a migration (no handle → handle) makes the BE force a
            // rotation at next login; mirror that on the row.
            if (typeof body?.mustChangePassword === "boolean") user = { ...user, mustChangePassword: body.mustChangePassword };
            toast.success(t("users_username_updated", { name: user.name }));
        }

        const data = {
            UID: user.UID,
            // WIRE: `new_username` is the DISPLAY NAME (legacy contract) —
            // the login handle went through /login/user/username above.
            new_username: user.name,
            new_permissions: user.permissions,
            new_warehouse: user.warehouse,
            new_userRFID: user.userRFID,
            // null clears the address; the BE only touches email when
            // the field is present in the body.
            new_email: user.email ?? null,
            // Single-session exemption. Only sent when the modal
            // actually offered the control (superAdmin actor) — the BE
            // treats undefined as "leave alone" and drops the field
            // outright for non-superAdmins.
            ...(user.allowConcurrentSessions === undefined
                ? {}
                : { new_allowConcurrentSessions: user.allowConcurrentSessions }),
            ...readActor(),
        };

        await apiFetch(`/login/user/edit`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        }).then(async (res) => {
            if (res.ok) {
                console.log(`USER ${user.UID} (${user.name}) has been updated`);

                // Update the row in place. Must be a NEW array: mutating `users` and setting
                // the same reference left React with nothing to re-render, so the badge kept
                // the old position until a reload (real-data rehearsal, 2026-09-23). Merge
                // rather than replace so list-only fields (lastActiveAt, status…) survive.
                set_Users(prev => prev.map((row) => (row.UID === user.UID ? { ...row, ...user } : row)));

                toggleEdit({ user: blankUser, changesMade: true });
            } else {
                // Guard rejections (invalid email, address already in
                // use, superAdmin rules) come back with a message —
                // surface it instead of silently keeping the modal open.
                const response = await res.json().catch(() => ({} as any));
                toast.error(response?.message || t("server_error"));
            }
        });
    };

    const usersContent = users.length == 0 ? (<div className="flex"><FlowLoader /></div>) :
        (<div>
            <AdminPageHeader
                titleKey="users_panel"
                backTo="/admin"
                hideSearch
                crumbs={[{ labelKey: "admin_panel", to: "/admin" }, { labelKey: "users" }]}
                actions={
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => set_Toggle_addUser(true)}
                        disabled={!canCreate}
                        aria-disabled={!canCreate}
                        title={readOnly ? t("read_only_mode") : !canCreate ? t("users_action_not_granted") : undefined}
                    >
                        {t("add_user")}
                    </button>
                }
            />

            <UserList
                users={users}
                toggleDelete={toggleDelete}
                toggleEdit={toggleEdit}
                requestForce={(u) => setPending({ kind: "force", user: u })}
                requestSuspend={(u) => setPending({ kind: "suspend", user: u })}
                requestUnsuspend={(u) => setPending({ kind: "unsuspend", user: u })}
            />
        </div>);

    return (
        <div className="page-data page-data-wide">

            {usersContent}

            {toggle_addUser && (
                <UserAdd addUser={addUser} closeFunction={closePopup} />
            )}

            {toggle_editUser && (
                <EditUser
                    user={liveEditUser}
                    editUser={editUser}
                    toggleEdit={toggleEdit}
                    requestForce={(u) => setPending({ kind: "force", user: u })}
                    requestSuspend={(u) => setPending({ kind: "suspend", user: u })}
                    requestUnsuspend={(u) => setPending({ kind: "unsuspend", user: u })}
                    toggleDelete={toggleDelete}
                    resetPassword={resetPassword}
                    resetTwoFactor={resetTwoFactor}
                />
            )}

            {toggle_deleteUser && (
                <DeleteUser
                    user={deleteUsersDetails}
                    deleteUser={deleteUser}
                    toggleDelete={toggleDelete}
                />
            )}

            {pending && (() => {
                const u = pending.user;
                const config = pending.kind === "force"
                    ? {
                        titleKey: "users_force_change_title",
                        bodyKey: "users_force_change_body",
                        confirmLabelKey: "users_force_change_confirm",
                        confirmTone: "primary" as const,
                        run: forceChange,
                    }
                    : pending.kind === "suspend"
                        ? {
                            titleKey: "users_suspend_title",
                            bodyKey: "users_suspend_body",
                            confirmLabelKey: "users_suspend_confirm",
                            confirmTone: "danger" as const,
                            run: suspend,
                        }
                        : {
                            titleKey: "users_unsuspend_title",
                            bodyKey: "users_unsuspend_body",
                            confirmLabelKey: "users_unsuspend_confirm",
                            confirmTone: "primary" as const,
                            run: unsuspend,
                        };
                return (
                    <UserActionConfirm
                        titleKey={config.titleKey}
                        bodyKey={config.bodyKey}
                        bodyParams={{ name: u.name }}
                        confirmLabelKey={config.confirmLabelKey}
                        confirmTone={config.confirmTone}
                        submitting={submitting}
                        onConfirm={async () => {
                            setSubmitting(true);
                            try {
                                await config.run(u);
                            } finally {
                                setSubmitting(false);
                                setPending(null);
                            }
                        }}
                        onCancel={() => {
                            if (!submitting) setPending(null);
                        }}
                    />
                );
            })()}

        </div>
    );
};

export default AdminUsers;
